import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { api, type SearchResult } from "./api";
import { useAccess } from "./access";
import { useBusiness } from "./business";
import { SOLUTION_SETTINGS } from "./solutions/settings";
import { Icon } from "./ui";

/**
 * The top-bar search: the field opens a panel in place with scope chips, recent searches, grouped results and the
 * business's pages. ⌘K / Ctrl+K opens it anywhere. Every switched-on solution adds what it can find.
 */

interface Hit { key: string; to: string; title: string; sub: string; group: string }

const RECENT_KEY = "mivio.recent-searches";
function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string").slice(0, 6) : [];
  } catch {
    return [];
  }
}
function writeRecent(list: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
  } catch {
    /* private windows: recent searches are a convenience */
  }
}

export function SearchPalette() {
  const nav = useNavigate();
  const { navItems, has } = useBusiness();
  const { canOpen, can } = useAccess();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [hits, setHits] = useState<Hit[]>([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const areas = useMemo(() => navItems.filter((n) => n.to !== "/" && canOpen(n.to)), [navItems, canOpen]);
  const settingsPages = useMemo(
    () => [
      { to: "/settings/general", label: "Settings › General" },
      { to: "/settings/solutions", label: "Settings › Solutions" },
      { to: "/settings/team", label: "Settings › Team" },
      { to: "/settings/roles", label: "Settings › Roles" },
      ...(can("business.connectors") ? [{ to: "/settings/connectors", label: "Settings › Connectors" }] : []),
      ...(can("business.payments") ? [{ to: "/settings/payments", label: "Settings › Payments" }] : []),
      { to: "/settings/files", label: "Settings › Files" },
      ...(can("business.timeline") ? [{ to: "/settings/timeline", label: "Settings › Timeline" }] : []),
      ...SOLUTION_SETTINGS.filter((x) => has(x.solution) && (!x.need || can(x.need))).map((x) => ({ to: `/settings/${x.key}`, label: `Settings › ${x.label}` })),
      { to: "/account/security", label: "Your login › Security" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [has],
  );

  const close = useCallback(() => {
    setOpen(false);
    setQ("");
    setHits([]);
    setActive(0);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;
    setRecent(readRecent());
    setTimeout(() => inputRef.current?.focus(), 0);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (!open || term.length < 1) {
      setHits((h) => (h.length ? [] : h));
      setBusy(false);
      return;
    }
    const mine = ++seq.current;
    setBusy(true);
    const t = setTimeout(async () => {
      const pages: Hit[] = [...areas.flatMap((a) => [{ to: a.to, label: a.label }, ...(a.subs ?? []).map((s) => ({ to: s.to, label: `${a.label} › ${s.label}` }))]), ...settingsPages]
        .filter((p) => p.label.toLowerCase().includes(term.toLowerCase()))
        .map((p) => ({ key: `page-${p.to}`, to: p.to, title: p.label, sub: "Page", group: "Pages" }));
      let found: Hit[] = [];
      if (term.length >= 2) {
        try {
          const r = await api<{ results: SearchResult[] }>(`/search?q=${encodeURIComponent(term)}`);
          found = r.results.map((x, i) => ({ key: `hit-${i}-${x.path}`, to: x.path, title: x.title, sub: x.subtitle ?? "", group: x.solution }));
        } catch {
          found = [];
        }
      }
      if (mine !== seq.current) return;
      const all = [...found, ...pages];
      setHits(kind === "all" ? all : all.filter((h) => h.group === kind));
      setActive(0);
      setBusy(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q, kind, open, areas, settingsPages]);

  const go = useCallback((to: string, remember?: string) => {
    if (remember) writeRecent([remember, ...readRecent().filter((x) => x !== remember)]);
    close();
    nav(to);
  }, [close, nav]);

  const groups = useMemo(() => {
    const out: { label: string; items: { h: Hit; index: number }[] }[] = [];
    hits.forEach((h, index) => {
      let g = out.find((x) => x.label === h.group);
      if (!g) {
        g = { label: h.group, items: [] };
        out.push(g);
      }
      g.items.push({ h, index });
    });
    return out;
  }, [hits]);
  const kinds = useMemo(() => ["all", ...areas.map((a) => a.label)], [areas]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(hits.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const h = hits[active];
      if (h) go(h.to, q.trim());
    }
  };
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

  return (
    <>
      <button type="button" className="topbar__searchbtn" onClick={() => setOpen(true)} aria-label="Search">
        <Icon.search />
        <span>Search</span>
        <kbd>{mac ? "⌘K" : "Ctrl K"}</kbd>
      </button>
      {open && (
        <div className="palette" role="dialog" aria-modal="true" aria-label="Search" onKeyDown={onKeyDown}>
          <button type="button" className="palette__scrim" aria-label="Close search" onClick={close} />
          <div className="palette__panel">
            <div className="palette__field">
              <Icon.search />
              <input ref={inputRef} type="search" value={q} placeholder="Search" autoComplete="off" spellCheck={false} onChange={(e) => setQ(e.target.value)} />
              {q && <button type="button" className="palette__clear" aria-label="Clear" onClick={() => setQ("")}>×</button>}
            </div>
            <div className="palette__kinds" role="tablist">
              {kinds.map((k) => (
                <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? "is-active" : ""} onClick={() => setKind(k)}>{k === "all" ? "All" : k}</button>
              ))}
            </div>
            <div className="palette__body">
              {!q.trim() ? (
                recent.length ? (
                  <>
                    <div className="palette__head">
                      <span>Recent searches</span>
                      <button type="button" onClick={() => { writeRecent([]); setRecent([]); }}>Clear history</button>
                    </div>
                    {recent.map((r) => (
                      <button key={r} type="button" className="palette__row" onClick={() => setQ(r)}>
                        <span className="palette__ico"><Icon.search /></span>
                        <span className="palette__text"><strong>{r}</strong></span>
                      </button>
                    ))}
                  </>
                ) : <p className="palette__hint">Search people, events, partners and every page of this business.</p>
              ) : busy && hits.length === 0 ? (
                <p className="palette__hint">Searching…</p>
              ) : hits.length === 0 ? (
                <p className="palette__hint">Nothing matches “{q.trim()}”.</p>
              ) : (
                groups.map((g) => (
                  <div key={g.label}>
                    <div className="palette__head"><span>{g.label}</span></div>
                    {g.items.map(({ h, index }) => (
                      <button key={h.key} type="button" className={`palette__row${index === active ? " is-active" : ""}`} onMouseEnter={() => setActive(index)} onClick={() => go(h.to, q.trim())}>
                        <span className="palette__ico">{h.group === "Pages" ? <Icon.pages /> : <Icon.search />}</span>
                        <span className="palette__text"><strong>{h.title}</strong>{h.sub && <span>{h.sub}</span>}</span>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
